import type { ILegalTextParagraph } from "~/models/ILegalTextParagraph";
import type { IToC } from "~/models/IToC";
import type { OpenState } from "~/composables/useLegalTextDisclosure";

export function useVirtualizedToC({
  flatToc,
  nestedToc,
  parentsOfActiveParagraph,
  legalTextParagraph,
  itemHeight,
}: {
  flatToc: Ref<(IToC | ILegalTextParagraph)[]>;
  nestedToc: Ref<IToC[]>;
  parentsOfActiveParagraph: Ref<IToC[]>;
  legalTextParagraph: Ref<ILegalTextParagraph | undefined>;
  itemHeight: number;
}) {
  const currentList = ref<(IToC | ILegalTextParagraph)[]>([]);

  const { wrapperProps, containerProps, list, scrollTo } = useVirtualList(
    currentList.value,
    {
      itemHeight,
    }
  );

  const openState = computed<OpenState>(() => {
    if (currentList.value.length === flatToc.value.length) {
      return "open";
    } else if (currentList.value.length === nestedToc.value.length) {
      return "closed";
    } else {
      return "mixed";
    }
  });

  const scrollToActiveParagraph = async () => {
    if (legalTextParagraph?.value) {
      await nextTick();
      const index = currentList.value.findIndex(
        (item) =>
          (item as ILegalTextParagraph).slug === legalTextParagraph.value?.slug
      );
      if (index !== -1) {
        // pad offset by 6 so we have some space to the top - but don't go lower than 0
        const offset = Math.max(0, index - 6);
        scrollTo(offset);
      } else {
        scrollTo(0);
      }
    } else {
      scrollTo(0);
    }
  };

  const isParentOfActiveParagraph = (toc: IToC) => {
    return parentsOfActiveParagraph.value.find((t) => t.id === toc.id);
  };

  const openParentsOfActiveParagraph = async () => {
    await nextTick();
    parentsOfActiveParagraph.value.forEach((parent) => {
      if (!tocOpen(parent)) {
        addToList(parent);
      }
    });
  };

  const tocOpen = (toc: IToC) =>
    currentList.value.some((i) => {
      const isParagraph = "paragraph" in i;
      if (isParagraph) {
        return toc.legal_text_paragraphs.some((p) => p.id === i.id);
      } else {
        return toc.children?.some((c) => c.id === i.id);
      }
    });

  const addToList = (toc: IToC) => {
    const index = currentList.value.indexOf(toc) + 1;
    currentList.value.splice(
      index,
      0,
      ...[...(toc.legal_text_paragraphs ?? []), ...(toc.children ?? [])]
    );
  };

  const removeFromList = (toc: IToC) => {
    removeParagraphs(toc);
    removeAllChildren(toc);
  };

  const handleTocClick = (toc: IToC) => {
    if (!tocOpen(toc)) {
      addToList(toc);
    } else {
      removeFromList(toc);
    }
  };

  const removeAllChildren = (toc: IToC) => {
    toc.children?.forEach((child) => {
      const index = currentList.value.indexOf(child);
      if (index !== -1) currentList.value.splice(index, 1);
      removeFromList(child);
    });
  };

  const removeParagraphs = (toc: IToC) => {
    toc.legal_text_paragraphs.forEach((paragraph) => {
      const index = currentList.value.indexOf(paragraph);
      if (index !== -1) currentList.value.splice(index, 1);
    });
  };

  function isParagraph(
    p: IToC | ILegalTextParagraph
  ): p is ILegalTextParagraph {
    return "paragraph" in p;
  }

  const openList = async () => {
    currentList.value.splice(0, currentList.value.length);
    currentList.value.push(...flatToc.value);
    await scrollToActiveParagraph();
  };

  const closeList = async () => {
    currentList.value.splice(0, currentList.value.length);
    if (nestedToc.value.length === 1) {
      // TODO: is this the same as flatToc?
      currentList.value.push(...nestedToc.value[0].legal_text_paragraphs);
    } else {
      currentList.value.push(...nestedToc.value);
    }
    scrollTo(0);
  };

  onMounted(openList);

  watch(flatToc, openList);

  watchEffect(async () => {
    // scroll to active paragraph if not visible
    if (!import.meta.env.SSR && legalTextParagraph?.value) {
      await nextTick();
      // the virtual list has a buffer that for some reason is 4 from the start and 7 from the end
      const activeSlice = list.value.slice(4, -7);
      // check if item is visible already, only trigger scrolling when it is not
      if (
        !activeSlice.find(
          (item) => item.data.slug === legalTextParagraph?.value?.slug
        )
      ) {
        await scrollToActiveParagraph();
      }
    }
  });

  watch(parentsOfActiveParagraph, openParentsOfActiveParagraph, {
    immediate: true,
  });

  return {
    handleTocClick,
    closeList,
    openList,
    tocOpen,
    isParagraph,
    isParentOfActiveParagraph,
    scrollToActiveParagraph,
    wrapperProps,
    containerProps,
    list,
    openState,
  };
}
